import type {
	AnySocketConnectionParams,
	EventKey,
	EventPayload,
	SocketServiceEventMap
} from '../types';

const CONTROLLER_KEY = '__NEWO_E2E_REALTIME__';

type RealtimeTestController = {
	connections: AnySocketConnectionParams[];
	connect: (params: AnySocketConnectionParams) => void;
	disconnect: (params: AnySocketConnectionParams | undefined) => void;
	recover: () => void;
	emit: (event: string, payload?: unknown) => void;
	getListenerCount: (event: string, connectorIdn?: string) => number;
	on: (
		event: string,
		handler: (payload: unknown) => void,
		getConnection: () => AnySocketConnectionParams | undefined
	) => () => void;
	reset: () => void;
};

type RegisteredHandler = {
	getConnection: () => AnySocketConnectionParams | undefined;
	handler: (payload: unknown) => void;
};

const getRealtimeTestController = (): RealtimeTestController => {
	const existing = Reflect.get(globalThis, CONTROLLER_KEY);
	if (existing) {
		return existing as RealtimeTestController;
	}

	const handlers = new Map<string, Set<RegisteredHandler>>();
	const connections: AnySocketConnectionParams[] = [];
	const pendingConnections = new Map<
		AnySocketConnectionParams,
		ReturnType<typeof globalThis.setTimeout>
	>();
	const emitForConnection = (
		event: string,
		connection: AnySocketConnectionParams,
		payload?: unknown
	) => {
		handlers.get(event)?.forEach((listener) => {
			if (listener.getConnection() === connection) {
				listener.handler(payload);
			}
		});
	};
	const controller = {
		connections,
		connect(params: AnySocketConnectionParams) {
			connections.push(params);
			const pendingConnection = globalThis.setTimeout(() => {
				pendingConnections.delete(params);
				emitForConnection('connect', params);
			}, 0);
			pendingConnections.set(params, pendingConnection);
		},
		disconnect(params: AnySocketConnectionParams | undefined) {
			if (!params) {
				return;
			}
			const pendingConnection = pendingConnections.get(params);
			if (pendingConnection) {
				globalThis.clearTimeout(pendingConnection);
				pendingConnections.delete(params);
			}
			emitForConnection('disconnect', params);
			handlers.forEach((listeners, event) => {
				listeners.forEach((listener) => {
					if (listener.getConnection() === params) {
						listeners.delete(listener);
					}
				});
				if (listeners.size === 0) {
					handlers.delete(event);
				}
			});
		},
		recover() {
			controller.emit('reconnect');
		},
		emit(event: string, payload?: unknown) {
			if (event === 'connect' || event === 'disconnect') {
				pendingConnections.forEach((pendingConnection) => {
					globalThis.clearTimeout(pendingConnection);
				});
				pendingConnections.clear();
			}
			const listeners = handlers.get(event);
			if (!listeners) {
				return;
			}
			const connectorIdn =
				typeof payload === 'object' && payload !== null
					? Reflect.get(payload, 'connector_idn')
					: undefined;
			for (const listener of listeners) {
				const connectionConnectorIdn = listener.getConnection()?.connectorIdn;
				if (
					typeof connectorIdn === 'string' &&
					connectionConnectorIdn &&
					connectionConnectorIdn !== connectorIdn
				) {
					continue;
				}
				listener.handler(payload);
			}
		},
		getListenerCount(event: string, connectorIdn?: string) {
			const listeners = handlers.get(event);
			if (!listeners) {
				return 0;
			}
			return Array.from(listeners).filter(
				(listener) =>
					!connectorIdn ||
					listener.getConnection()?.connectorIdn === connectorIdn
			).length;
		},
		on(
			event: string,
			handler: (payload: unknown) => void,
			getConnection: () => AnySocketConnectionParams | undefined
		) {
			const listeners = handlers.get(event) ?? new Set();
			const listener = { getConnection, handler };
			listeners.add(listener);
			handlers.set(event, listeners);
			return () => {
				listeners.delete(listener);
				if (listeners.size === 0) {
					handlers.delete(event);
				}
			};
		},
		reset() {
			pendingConnections.forEach((pendingConnection) => {
				globalThis.clearTimeout(pendingConnection);
			});
			pendingConnections.clear();
			connections.length = 0;
			handlers.clear();
		}
	};
	Reflect.set(globalThis, CONTROLLER_KEY, controller);
	return controller;
};

const isE2ERealtimeMockEnabled = () => {
	const environment = Reflect.get(globalThis, '__APP_ENV__');
	return Reflect.get(environment ?? {}, 'VITE_E2E_REALTIME_MOCK') === 'true';
};

class E2ERealtimeTransport<TEventMap extends SocketServiceEventMap> {
	private readonly controller = getRealtimeTestController();
	private connection: AnySocketConnectionParams | undefined;

	connect(params: AnySocketConnectionParams) {
		this.connection = params;
		this.controller.connect(params);
		return undefined as never;
	}

	disconnect() {
		this.controller.disconnect(this.connection);
		this.connection = undefined;
	}

	on<K extends EventKey<TEventMap>>(
		event: K,
		handler: (payload: EventPayload<TEventMap, K>) => void
	) {
		return this.controller.on(
			event as string,
			handler as (payload: unknown) => void,
			() => this.connection
		);
	}
}

export {
	E2ERealtimeTransport,
	getRealtimeTestController,
	isE2ERealtimeMockEnabled,
	type RealtimeTestController
};
